/**
 * Browser cache for review material previews.
 *
 * A revision's file never changes, and its preview URL names the revision, so
 * once downloaded it can be reused on every later visit instead of streaming
 * it through the API from VaultSage again. Cache Storage rather than
 * localStorage: files are often larger than localStorage's ~5 MB, it stores
 * binary as-is instead of base64 text, and it does not block the page.
 *
 * Only read after the review itself has loaded, so the server has already
 * confirmed this user may see it. Cleared on logout, for shared computers.
 */

const CACHE_NAME = "cs-material-preview-v1";
// Recent files kept; the oldest is evicted beyond this.
const MAX_ENTRIES = 30;

const available = () => typeof caches !== "undefined";

async function store(url: string, blob: Blob) {
  try {
    const cache = await caches.open(CACHE_NAME);
    await cache.put(url, new Response(blob, { headers: { "Content-Type": blob.type } }));
    const keys = await cache.keys();
    // keys() is in insertion order, so the front holds the oldest files.
    await Promise.all(keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES)).map((key) => cache.delete(key)));
  } catch {
    // Quota exceeded or storage disabled: the preview still works uncached.
  }
}

export async function loadMaterial(url: string, signal?: AbortSignal): Promise<Blob> {
  if (available()) {
    try {
      const hit = await (await caches.open(CACHE_NAME)).match(url);
      if (hit) return await hit.blob();
    } catch {
      // Fall through to the network.
    }
  }
  const response = await fetch(url, { credentials: "same-origin", signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  if (available()) void store(url, blob);
  return blob;
}

/** Drop one cached file, e.g. after it failed to parse, so the next try refetches. */
export async function evictMaterial(url: string): Promise<void> {
  if (!available()) return;
  try {
    await (await caches.open(CACHE_NAME)).delete(url);
  } catch {
    // Storage disabled: nothing was cached.
  }
}

export async function clearMaterialCache(): Promise<void> {
  if (!available()) return;
  await caches.delete(CACHE_NAME).catch(() => false);
}
